/**
 * Logo Mockup Preview — pure canvas version, on-canvas handle controls.
 * No APIs, no keys, no backend. Customer uploads a logo, then interacts
 * directly with it on the canvas using a standard selection-box UI:
 *   - drag the logo body                 -> move
 *   - drag a corner square                -> resize (aspect-ratio locked)
 *   - drag a top/bottom edge square       -> stretch height only
 *   - drag a left/right edge square       -> stretch width only
 *   - drag the handle above the box       -> rotate
 *   - drag the handle below the box       -> skew (shear)
 * Hovering each handle shows the matching cursor (resize arrows, grab,
 * etc.) so it's clear what dragging it will do.
 */
(function () {
  // These are defined in real screen (CSS) pixels, then scaled up to
  // canvas-resolution pixels at runtime — otherwise handles would render
  // tiny on high-resolution product photos displayed at a smaller CSS
  // size (the usual case on mobile).
  const HANDLE_HIT_RADIUS_CSS = 26; // touch-friendly, ~44px target diameter
  const HANDLE_SIZE_CSS = 14;
  const ROTATE_OFFSET_CSS = 40;
  const SKEW_OFFSET_CSS = 40;
  const MIN_HALF_WIDTH = 15;
  const MIN_HALF_HEIGHT = 15;
  const MAX_SKEW_DEG = 60;

  function initLogoMockup(root) {
    const fileInput = root.querySelector('[data-logo-input]');
    const canvasWrap = root.querySelector('[data-canvas-wrap]');
    const canvas = root.querySelector('[data-canvas]');
    const actions = root.querySelector('[data-actions]');
    const disclaimer = root.querySelector('[data-disclaimer]');
    const downloadBtn = root.querySelector('[data-download-btn]');
    const resetBtn = root.querySelector('[data-reset-btn]');
    const colorActions = root.querySelector('[data-color-actions]');
    const swatchButtons = root.querySelectorAll('[data-color-swatch]');
    const originalThumb = root.querySelector('[data-color-thumb-original]');

    const ctx = canvas.getContext('2d');
    const productImageUrl = root.dataset.productImage;
    const watermarkImageUrl = root.dataset.watermarkImage;

    const printArea = {
      top: parseFloat(root.dataset.printTop) / 100,
      left: parseFloat(root.dataset.printLeft) / 100,
      width: parseFloat(root.dataset.printWidth) / 100,
      height: parseFloat(root.dataset.printHeight) / 100,
    };

    let productImg = null;
    let logoImg = null;
    let aspect = 1;
    let selected = true; // whether the box/handles are currently shown
    let originalLogoDataUrl = null; // pristine upload, used as the source for black/white filtering
    let watermarkImg = null; // recolored-to-black watermark, drawn bottom-right once loaded

    // Load the watermark once, recolor it solid black (preserving alpha),
    // and keep it ready to stamp onto every render.
    (function loadWatermark() {
      if (!watermarkImageUrl) return;
      const raw = new Image();
      raw.crossOrigin = 'anonymous';
      raw.onload = () => {
        try {
          const off = document.createElement('canvas');
          off.width = raw.naturalWidth;
          off.height = raw.naturalHeight;
          const offCtx = off.getContext('2d');
          offCtx.drawImage(raw, 0, 0);

          const imageData = offCtx.getImageData(0, 0, off.width, off.height);
          const data = imageData.data;
          for (let i = 0; i < data.length; i += 4) {
            data[i] = 0;
            data[i + 1] = 0;
            data[i + 2] = 0;
            // alpha (data[i + 3]) left untouched
          }
          offCtx.putImageData(imageData, 0, 0);

          const finalImg = new Image();
          finalImg.onload = () => {
            watermarkImg = finalImg;
            draw();
          };
          finalImg.src = off.toDataURL('image/png');
        } catch (err) {
          // Cross-origin recolor blocked by CORS (canvas tainted) — fall
          // back to drawing the original file as-is, unrecolored.
          console.warn('Logo mockup: watermark recolor failed, using original file instead.', err);
          watermarkImg = raw;
          draw();
        }
      };
      raw.onerror = () => {
        console.warn('Logo mockup: watermark image failed to load from', watermarkImageUrl);
      };
      raw.src = watermarkImageUrl;
    })();

    // Logo transform state, in canvas pixel space.
    const state = {
      centerX: 0,
      centerY: 0,
      width: 0,
      height: 0,
      rotationDeg: 0,
      skewDeg: 0,
    };

    let dragMode = null; // 'move' | 'corner' | 'edge-v' | 'edge-h' | 'rotate' | 'skew' | null
    let dragOffsetX = 0;
    let dragOffsetY = 0;

    // --- Helper: find whichever product image is currently active in the
    // gallery (swiper-based media gallery on this theme). Falls back to
    // the image passed in from Liquid if nothing active is found. ---
    function getActiveProductImageUrl() {
      const activeSlide = document.querySelector(
        '.product__media-item.swiper-slide-active[data-media-type="image"], .product__media-item.is-active[data-media-type="image"]'
      );

      if (activeSlide) {
        const img = activeSlide.querySelector('img');
        let src = activeSlide.dataset.src || (img && (img.currentSrc || img.src));
        if (src) {
          if (src.startsWith('//')) src = 'https:' + src;
          return src;
        }
      }

      return productImageUrl; // fallback: the image the section loaded with
    }

    fileInput.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const logoDataUrl = reader.result;
        originalLogoDataUrl = logoDataUrl;
        const activeImageUrl = getActiveProductImageUrl();

        const freshBaseImg = new Image();
        freshBaseImg.crossOrigin = 'anonymous';
        freshBaseImg.onload = () => {
          productImg = freshBaseImg;
          canvas.width = freshBaseImg.naturalWidth;
          canvas.height = freshBaseImg.naturalHeight;

          const img = new Image();
          img.onload = () => {
            logoImg = img;
            aspect = img.naturalHeight / img.naturalWidth;
            selected = true;

            const printW = printArea.width * canvas.width;
            const printH = printArea.height * canvas.height;
            state.width = printW * 0.7;
            state.height = state.width * aspect;
            state.centerX = (printArea.left * canvas.width) + printW / 2;
            state.centerY = (printArea.top * canvas.height) + printH / 2;
            state.rotationDeg = 0;
            state.skewDeg = 0;

            canvasWrap.hidden = false;
            actions.hidden = false;
            resetBtn.hidden = false;
            colorActions.hidden = false;
            disclaimer.hidden = false;

            originalThumb.src = logoDataUrl;
            setActiveSwatch('original');

            draw();
          };
          img.src = logoDataUrl;
        };
        freshBaseImg.src = activeImageUrl;
      };
      reader.readAsDataURL(file);
    });

    downloadBtn.addEventListener('click', () => {
      draw({ withHandles: false });
      const url = canvas.toDataURL('image/png');
      draw({ withHandles: true });

      const link = document.createElement('a');
      link.download = 'logo-esikatselu.png';
      link.href = url;
      link.click();
    });

    swatchButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.colorSwatch;
        setActiveSwatch(mode);
        applyRecolor(mode);
      });
    });

    function setActiveSwatch(mode) {
      swatchButtons.forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.colorSwatch === mode);
      });
    }

    // Recolors the logo to solid black, solid white, or restores the
    // original upload — while preserving the alpha channel (so
    // transparency/edges stay intact). Always filters from the original
    // upload, not the currently-displayed image, so switching between
    // swatches repeatedly never degrades quality.
    function applyRecolor(mode) {
      if (!originalLogoDataUrl) return;

      if (mode === 'original') {
        const img = new Image();
        img.onload = () => {
          logoImg = img;
          draw();
        };
        img.src = originalLogoDataUrl;
        return;
      }

      const sourceImg = new Image();
      sourceImg.onload = () => {
        const offCanvas = document.createElement('canvas');
        offCanvas.width = sourceImg.naturalWidth;
        offCanvas.height = sourceImg.naturalHeight;
        const offCtx = offCanvas.getContext('2d');
        offCtx.drawImage(sourceImg, 0, 0);

        const imageData = offCtx.getImageData(0, 0, offCanvas.width, offCanvas.height);
        const data = imageData.data;
        const value = mode === 'black' ? 0 : 255;
        for (let i = 0; i < data.length; i += 4) {
          data[i] = value;
          data[i + 1] = value;
          data[i + 2] = value;
          // alpha (data[i + 3]) left untouched
        }
        offCtx.putImageData(imageData, 0, 0);

        const recolored = new Image();
        recolored.onload = () => {
          logoImg = recolored;
          draw();
        };
        recolored.src = offCanvas.toDataURL('image/png');
      };
      sourceImg.src = originalLogoDataUrl;
    }

    resetBtn.addEventListener('click', resetTool);

    function resetTool() {
      logoImg = null;
      productImg = null;
      dragMode = null;
      originalLogoDataUrl = null;
      selected = true;

      state.centerX = 0;
      state.centerY = 0;
      state.width = 0;
      state.height = 0;
      state.rotationDeg = 0;
      state.skewDeg = 0;

      fileInput.value = ''; // lets the same file be re-selected and still fire 'change'
      canvas.style.cursor = 'default';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      canvasWrap.hidden = true;
      actions.hidden = true;
      resetBtn.hidden = true;
      colorActions.hidden = true;
      disclaimer.hidden = true;
      setActiveSwatch('original');
    }

    function getDisplayScale() {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width) return 1;
      return canvas.width / rect.width;
    }

    // --- Transform helpers ---
    function getMatrix() {
      const theta = (state.rotationDeg * Math.PI) / 180;
      const shear = Math.tan((state.skewDeg * Math.PI) / 180);
      const cos = Math.cos(theta);
      const sin = Math.sin(theta);
      return {
        a: cos,
        b: sin,
        c: cos * shear - sin,
        d: sin * shear + cos,
      };
    }

    function localToCanvas(lx, ly) {
      const m = getMatrix();
      return {
        x: m.a * lx + m.c * ly + state.centerX,
        y: m.b * lx + m.d * ly + state.centerY,
      };
    }

    // Inverse of the full (rotation + skew) matrix — used for the move/box hit test.
    function canvasToLocal(px, py) {
      const m = getMatrix();
      const det = m.a * m.d - m.b * m.c;
      const dx = px - state.centerX;
      const dy = py - state.centerY;
      return {
        x: (m.d * dx - m.c * dy) / det,
        y: (-m.b * dx + m.a * dy) / det,
      };
    }

    // Inverse of rotation ONLY (ignores skew) — used to interpret drags on
    // the resize/skew handles in a stable, predictable direction.
    function canvasToDerotated(px, py) {
      const theta = (state.rotationDeg * Math.PI) / 180;
      const cos = Math.cos(theta);
      const sin = Math.sin(theta);
      const dx = px - state.centerX;
      const dy = py - state.centerY;
      return {
        x: cos * dx + sin * dy,
        y: -sin * dx + cos * dy,
      };
    }

    // All interactive handles: 4 corners + 4 edge midpoints (standard
    // selection-box style) plus the external rotate/skew handles.
    function getHandles() {
      const halfW = state.width / 2;
      const halfH = state.height / 2;
      const scale = getDisplayScale();
      const rotateOffset = ROTATE_OFFSET_CSS * scale;
      const skewOffset = SKEW_OFFSET_CSS * scale;
      return [
        { id: 'tl', lx: -halfW, ly: -halfH, cursor: 'nwse-resize', mode: 'corner' },
        { id: 'tr', lx: halfW, ly: -halfH, cursor: 'nesw-resize', mode: 'corner' },
        { id: 'br', lx: halfW, ly: halfH, cursor: 'nwse-resize', mode: 'corner' },
        { id: 'bl', lx: -halfW, ly: halfH, cursor: 'nesw-resize', mode: 'corner' },
        { id: 'top', lx: 0, ly: -halfH, cursor: 'ns-resize', mode: 'edge-v' },
        { id: 'bottom', lx: 0, ly: halfH, cursor: 'ns-resize', mode: 'edge-v' },
        { id: 'left', lx: -halfW, ly: 0, cursor: 'ew-resize', mode: 'edge-h' },
        { id: 'right', lx: halfW, ly: 0, cursor: 'ew-resize', mode: 'edge-h' },
        { id: 'rotate', lx: 0, ly: -halfH - rotateOffset, cursor: 'grab', mode: 'rotate' },
        { id: 'skew', lx: 0, ly: halfH + skewOffset, cursor: 'ew-resize', mode: 'skew' },
      ].map((h) => ({ ...h, canvasPos: localToCanvas(h.lx, h.ly) }));
    }

    function canvasPointFromEvent(e) {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    }

    function dist(p1, p2) {
      return Math.hypot(p1.x - p2.x, p1.y - p2.y);
    }

    function findHandleAt(pos) {
      if (!logoImg) return null;
      const handles = getHandles();
      const hitRadius = HANDLE_HIT_RADIUS_CSS * getDisplayScale();
      for (const h of handles) {
        if (dist(pos, h.canvasPos) <= hitRadius) return h;
      }
      return null;
    }

    // --- Pointer interaction ---
    canvas.addEventListener('pointerdown', (e) => {
      if (!logoImg) return;
      const pos = canvasPointFromEvent(e);

      if (!selected) {
        // First tap after being deselected just re-selects (shows the
        // box/handles again); a second, separate interaction is needed
        // to actually move/resize/rotate/skew it.
        selected = true;
        draw();
        return;
      }

      const handle = findHandleAt(pos);

      if (handle) {
        dragMode = handle.mode;
        canvas.style.cursor = handle.mode === 'move' ? 'grabbing' : handle.cursor;
      } else {
        const local = canvasToLocal(pos.x, pos.y);
        const halfW = state.width / 2;
        const halfH = state.height / 2;
        if (Math.abs(local.x) <= halfW && Math.abs(local.y) <= halfH) {
          dragMode = 'move';
          dragOffsetX = pos.x - state.centerX;
          dragOffsetY = pos.y - state.centerY;
          canvas.style.cursor = 'grabbing';
        } else {
          // tapped/clicked outside the logo entirely -> deselect
          selected = false;
          draw();
        }
      }

      if (dragMode) {
        canvas.setPointerCapture(e.pointerId);
      }
    });

    canvas.addEventListener('pointermove', (e) => {
      const pos = canvasPointFromEvent(e);

      if (!dragMode) {
        // hover only: update cursor to hint what dragging would do
        updateHoverCursor(pos);
        return;
      }
      if (!logoImg) return;

      if (dragMode === 'move') {
        state.centerX = pos.x - dragOffsetX;
        state.centerY = pos.y - dragOffsetY;
      } else if (dragMode === 'corner') {
        const derot = canvasToDerotated(pos.x, pos.y);
        const newHalfWidth = Math.max(MIN_HALF_WIDTH, Math.abs(derot.x));
        state.width = newHalfWidth * 2;
        state.height = state.width * aspect;
      } else if (dragMode === 'edge-v') {
        const derot = canvasToDerotated(pos.x, pos.y);
        const newHalfHeight = Math.max(MIN_HALF_HEIGHT, Math.abs(derot.y));
        state.height = newHalfHeight * 2;
      } else if (dragMode === 'edge-h') {
        const derot = canvasToDerotated(pos.x, pos.y);
        const newHalfWidth = Math.max(MIN_HALF_WIDTH, Math.abs(derot.x));
        state.width = newHalfWidth * 2;
      } else if (dragMode === 'rotate') {
        const angleRad = Math.atan2(pos.y - state.centerY, pos.x - state.centerX);
        state.rotationDeg = (angleRad * 180) / Math.PI + 90;
      } else if (dragMode === 'skew') {
        const halfH = state.height / 2;
        const skewOffset = SKEW_OFFSET_CSS * getDisplayScale();
        const derot = canvasToDerotated(pos.x, pos.y);
        const shear = derot.x / (halfH + skewOffset);
        let deg = (Math.atan(shear) * 180) / Math.PI;
        deg = Math.max(-MAX_SKEW_DEG, Math.min(MAX_SKEW_DEG, deg));
        state.skewDeg = deg;
      }

      draw();
    });

    function updateHoverCursor(pos) {
      if (!logoImg) {
        canvas.style.cursor = 'default';
        return;
      }
      if (!selected) {
        canvas.style.cursor = 'pointer';
        return;
      }
      const handle = findHandleAt(pos);
      if (handle) {
        canvas.style.cursor = handle.cursor;
        return;
      }
      const local = canvasToLocal(pos.x, pos.y);
      const halfW = state.width / 2;
      const halfH = state.height / 2;
      if (Math.abs(local.x) <= halfW && Math.abs(local.y) <= halfH) {
        canvas.style.cursor = 'grab';
      } else {
        canvas.style.cursor = 'default';
      }
    }

    function endDrag() {
      dragMode = null;
      canvas.style.cursor = 'default';
    }
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);

    // --- Rendering ---
    function draw(opts) {
      const withHandles = !opts || opts.withHandles !== false;

      if (!productImg) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(productImg, 0, 0, canvas.width, canvas.height);

      if (!logoImg) return;

      const m = getMatrix();
      ctx.setTransform(m.a, m.b, m.c, m.d, state.centerX, state.centerY);
      ctx.globalAlpha = 0.90;
      ctx.drawImage(logoImg, -state.width / 2, -state.height / 2, state.width, state.height);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      drawWatermark();

      if (withHandles && selected) {
        drawOverlay();
      }
    }

    function drawWatermark() {
      if (!watermarkImg) return;
      const wmWidth = canvas.width * 0.15;
      const wmHeight = wmWidth * (watermarkImg.naturalHeight / watermarkImg.naturalWidth);
      const padding = canvas.width * 0.02;
      const x = canvas.width - wmWidth - padding;
      const y = canvas.height - wmHeight - padding;

      ctx.save();
      ctx.globalAlpha = 0.1;
      ctx.drawImage(watermarkImg, x, y, wmWidth, wmHeight);
      ctx.restore();
    }

    function drawOverlay() {
      const halfW = state.width / 2;
      const halfH = state.height / 2;
      const corners = [
        localToCanvas(-halfW, -halfH),
        localToCanvas(halfW, -halfH),
        localToCanvas(halfW, halfH),
        localToCanvas(-halfW, halfH),
      ];
      const topCenter = localToCanvas(0, -halfH);
      const bottomCenter = localToCanvas(0, halfH);
      const handles = getHandles();
      const rotateHandle = handles.find((h) => h.id === 'rotate');
      const skewHandle = handles.find((h) => h.id === 'skew');

      ctx.save();

      const scale = getDisplayScale();

      // solid selection box
      ctx.strokeStyle = '#4285f4';
      ctx.lineWidth = 1 * scale;
      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < corners.length; i++) ctx.lineTo(corners[i].x, corners[i].y);
      ctx.closePath();
      ctx.stroke();

      // thin connector lines to the rotate/skew handles
      ctx.beginPath();
      ctx.moveTo(topCenter.x, topCenter.y);
      ctx.lineTo(rotateHandle.canvasPos.x, rotateHandle.canvasPos.y);
      ctx.moveTo(bottomCenter.x, bottomCenter.y);
      ctx.lineTo(skewHandle.canvasPos.x, skewHandle.canvasPos.y);
      ctx.strokeStyle = '#9aa0a6';
      ctx.lineWidth = 1 * scale;
      ctx.stroke();

      handles.forEach((h) => drawHandle(h.canvasPos));

      ctx.restore();
    }

    function drawHandle(pos) {
      const size = HANDLE_SIZE_CSS * getDisplayScale();
      const half = size / 2;
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#4285f4';
      ctx.lineWidth = 1.5 * getDisplayScale();
      ctx.fillRect(pos.x - half, pos.y - half, size, size);
      ctx.strokeRect(pos.x - half, pos.y - half, size, size);
    }
  }

  document.querySelectorAll('[data-logo-mockup]').forEach(initLogoMockup);
})();